"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Lock } from "lucide-react";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";

export interface SidebarItem {
  id: string;
  label: string;
  ariaLabel?: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  badge?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  /** Locked-feature upsell: hover shows a tooltip, click opens the upsell dialog (via onClick). */
  lockedUpsell?: { title: string; description: string };
}

/** Sends a sidebar or account-menu interaction to GA4 (funnels) and as a Sentry breadcrumb (error trails). */
export function trackSidebarEvent(category: "Account Menu" | "Sidebar", action: string, label?: string): void {
  const funnelAction = `${category}_${action}`.toLowerCase().replace(/[^a-z0-9]+/g, "_");
  trackFunnel("sidebar", funnelAction, label ? { label } : undefined);
  pushDataLayer(category, action, label);
}

export interface SidebarSection {
  id: string;
  label?: string;
  items: SidebarItem[];
}

export function SidebarTooltip({
  children,
  label,
  show,
}: {
  children: React.ReactNode;
  label: string;
  show: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  if (!show) return <>{children}</>;

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && (
        <div className="absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-3 py-1.5 text-xs font-semibold text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 duration-100">
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-border" />
        </div>
      )}
    </div>
  );
}

export function LockedItemTooltip({
  children,
  title,
  description,
  ctaLabel,
  ctaHref,
  onCtaClick,
}: {
  children: React.ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
  /** When omitted, the CTA renders as a button that fires onCtaClick instead of navigating. */
  ctaHref?: string;
  onCtaClick?: () => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isHovered || !wrapperRef.current) {
      setCoords(null);
      return;
    }
    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top + rect.height / 2,
      left: rect.right + 12, // 12px = ml-3
    });
  }, [isHovered]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && coords && typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: coords.top, left: coords.left, transform: "translateY(-50%)" }}
            className="fixed z-[100] w-60 rounded-md border border-border bg-popover p-3 shadow-md animate-in fade-in-0 zoom-in-95 duration-100"
          >
            <div className="mb-1 flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold text-foreground">{title}</span>
            </div>
            <p className="mb-2.5 text-xs text-muted-foreground">{description}</p>
            {ctaHref ? (
              <Link
                to={ctaHref}
                onClick={onCtaClick}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {ctaLabel} <ArrowRight className="h-3 w-3" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={onCtaClick}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {ctaLabel} <ArrowRight className="h-3 w-3" />
              </button>
            )}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-border" />
          </div>,
          document.body
        )}
    </div>
  );
}

export function DropdownMenuSub({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        className="flex w-full items-center px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
        onClick={() => setOpen((value) => !value)}
      >
        {icon}
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-full top-0 z-50 ml-1 max-h-[320px] min-w-[200px] overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-md animate-in fade-in-0 zoom-in-95 duration-100">
          {children}
        </div>
      )}
    </div>
  );
}
